import { useEffect, useState, type FC, useRef } from "react";
import type { User } from "@/UserContext";
import { useMsal } from "@azure/msal-react";
import { useNavigate, useLocation, Routes, Route, Navigate } from "react-router-dom";
import { WelcomeScreen } from "./WelcomeScreen";
import DeviceSelection from "./DeviceSelection";
import AuthenticatorSetupScreen from "./Authenticator/AuthenticatorSetupScreen";
import CompanyPortalInstall from "./CompanyPortalInstall";
import AppDownloadScreen from "./AppDownloadScreen";
import AuthRedirectHandler from "./AuthRedirectHandler";
import FeedbackThanksScreen from "./Feedback/FeedbackThanksScreen";
import FeedbackSubmittedScreen from "./Feedback/FeedbackSubmittedScreen";
import RetryScreen from "./common/RetryScreen";
import ErrorScreen from "./common/ErrorScreen"; 
import LoadingScreen from "./common/LoadingScreen";
import { UserContext } from "../UserContext";
import Footer from "./common/Footer";
import { PolicyCacheService } from '../utils/policyCacheService';
import { ROUTES } from '@/constants/routes';
import { t } from "@/strings/index";
import { getFeatureFlag } from '../services/ecs';
import { useAbandonmentTracking } from '../hooks/useAbandonmentTracking';
import { useBrowserBackTracking } from '../hooks/useBrowserBackTracking';
import TelemetryErrorBoundary from './TelemetryErrorBoundary';
import { logDev as debugLog, warnDev } from '@/logging/devConsole';
import '@/styles/AppLayout.css';

const App: React.FC = () => {
  const { accounts, inProgress, instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();

  const enablePreviewTag = getFeatureFlag('enablePreviewTag', false);

  // Determine if we should clear user context (on final thank you screen or after logout)
  const isFeedbackScreen = location.pathname === ROUTES.FEEDBACK_THANKS;
  const hasActiveAccountId = sessionStorage.getItem('msal.activeAccountId') !== null;

  // Derive the current user account every render from MSAL and persisted ID
  const savedId = sessionStorage.getItem('msal.activeAccountId');
  const fromSave = savedId ? accounts.find(a => a.localAccountId === savedId) : null;
  const active = instance.getActiveAccount();
  console.log('MSAL accounts changed, updating user context. SavedId:', savedId, 'fromSave:', fromSave, 'active:', active);
  
  // Keep user context on feedback screen for SDK, only clear if no active account ID saved (user logged out)
  const user: User | null = !hasActiveAccountId 
    ? null 
    : (fromSave || active || (accounts.length ? accounts[accounts.length - 1] : null));
  
  // Track previous user to detect changes
  const previousUserIdRef = useRef<string | null>(null);

  console.log('Current user context:', user);
  
  const [ecsInitialized, setEcsInitialized] = useState(false);

  // Initialize abandonment tracking
  useAbandonmentTracking();

  // Track browser back/forward button usage
  useBrowserBackTracking();

  debugLog("MSAL accounts:", accounts, "inProgress:", inProgress);

  // Clear policy cache when user changes
  useEffect(() => {
    const currentUserId = user?.localAccountId || user?.homeAccountId || null;
    
    if (previousUserIdRef.current !== null && previousUserIdRef.current !== currentUserId) {
      debugLog('User context changed, clearing policy cache. Previous:', previousUserIdRef.current, 'Current:', currentUserId);
      PolicyCacheService.clearCache();
    }
    
    previousUserIdRef.current = currentUserId;
  }, [user]);

  // Update document title based on current route
  useEffect(() => {
    const baseTitle = location.pathname === ROUTES.HOME
      ? t('app.welcome.title')
      : t('app.title');

    document.title = enablePreviewTag
      ? `${baseTitle} (${t('app.previewTitle')})`
      : baseTitle;
  }, [location, enablePreviewTag]);

  // Create unique key for RetryScreen based on step to force remounting
  const RetryScreenWithKey = () => {
    const retryStep = location.state?.step || 'unknown';
    const retryCount = location.state?.retryCount || 0;
    return <RetryScreen key={`retry-${retryStep}-${retryCount}`} />;
  };

  // Check kill switch - if enabled, show error screen
  const killSwitchEnabled = getFeatureFlag('killSwitch', false);
  if (killSwitchEnabled) {
    return <ErrorScreen />;
  }

  const isWelcomeRoute = location.pathname === ROUTES.HOME;
 // FEEDBACK_THANKS
 // ROUTES.FEEDBACK_THANKS on line 109 instead of ROUTES.DEVICE_SELECTION
 // ROUTES.COMPANY_PORTAL_INSTALL on line 113 is og one
  return (
    <TelemetryErrorBoundary>
      <UserContext.Provider value={user}>
          <div className={isWelcomeRoute ? "app-shell" : "app-shell shared-background-wrapper"}>
            <div className="app-content">
              <Routes>
            <Route path={ROUTES.HOME} element={<WelcomeScreen onGetStarted={() => navigate(ROUTES.DEVICE_SELECTION)} />} /> 
            <Route path={ROUTES.DEVICE_SELECTION} element={<DeviceSelection />} />
            <Route path={ROUTES.AUTH_SETUP} element={<AuthenticatorSetupScreen />} />
            <Route path={ROUTES.COMPANY_PORTAL_INSTALL} element={<CompanyPortalInstall />} />
            <Route path={ROUTES.FEEDBACK_THANKS} element={<FeedbackThanksScreen />} />
            <Route path={ROUTES.FEEDBACK_SUBMITTED} element={<FeedbackSubmittedScreen />} />
            <Route path={ROUTES.APP_DOWNLOAD} element={<AppDownloadScreen />} />
            <Route path={ROUTES.AUTH_REDIRECT} element={<AuthRedirectHandler />} />
            <Route path={ROUTES.RETRY} element={<RetryScreenWithKey />} />
            <Route path={ROUTES.ERROR} element={<ErrorScreen />} />
            <Route path={ROUTES.LOADING} element={<LoadingScreen />} />
            <Route path="*" element={<Navigate to={ROUTES.HOME} />} />
              </Routes>
            </div>
            {/* show footer on all screens except welcome */}
            {location.pathname !== ROUTES.HOME && <Footer />}
          </div>
      </UserContext.Provider>
    </TelemetryErrorBoundary>
  );
};
export default App;
